import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AppInput,
  AppMultiSelect,
  AppRemixIcon,
  AppSingleSelect,
} from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import ReactDOMServer from 'react-dom/server';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import Translator from '../../../../components/helpers/Translator';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import { getTotalEnergiesRiskRiskFilterInitialValues } from '../../../../../minions/forms';
import AuxSidebar from '../../../../Sidebar/AuxSidebar';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import TotalEnergiesRiskProcessSelect from '../../../../components/form/Selects/TotalEnergiesRiskProcessSelect';
import {
  getInherentImpact,
  getProbability,
  getSeverity,
  getRiskState,
  getRiskType,
  getPriority,
} from '../tools/totalEnergiesRiskRiskTools';
import TotalEnergiesRiskRiskSelect from '../../../../components/form/Selects/TotalEnergiesRiskRiskSelect';

export default function RiskFilterSidebarBlock() {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { moduleBridgeHex, filtersHex } = useParams();
  const auxSidebar = useSelector(
    (state) => state.currentPage?.auxSidebar?.opened
  );
  const permissionLevel = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.userBridge?.permissions?.level
  );
  const filters = useSelector((state) => state.currentPage?.riskFilters);
  const [openOnce, setOpenOnce] = useState(true);
  const [noYesOptions] = useState([
    {
      label: <Translator file="user" name="general.no" />,
      value: 0,
    },
    {
      label: <Translator file="user" name="general.yes" />,
      value: 1,
    },
  ]);
  const [riskTypeOptions] = useState(
    Array.from({ length: 8 }, (_, i) => ({ value: i, label: getRiskType(i) }))
  );
  const [stateOptions] = useState(
    Array.from({ length: 6 }, (_, i) => ({ value: i, label: getRiskState(i) }))
  );
  const [probabilityOptions] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getProbability(i),
    }))
  );
  const [inherentImpactOptions] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      value: i,
      label: getInherentImpact(i),
    }))
  );
  const [severityOptions] = useState(
    Array.from({ length: 4 }, (_, i) => ({
      value: i,
      label: getSeverity(i),
    }))
  );
  const [priorityOptions] = useState(
    Array.from({ length: 4 }, (_, i) => ({
      value: i,
      label: getPriority(i),
    }))
  );

  const handleAuxSidebar = () => {
    if (!openOnce) setOpenOnce(true);
    if (auxSidebar !== 'filters') {
      dispatch(currentPageAction.change('auxSidebar', { opened: 'filters' }));
    } else {
      dispatch(currentPageAction.change('auxSidebar', { opened: null }));
    }
  };

  const goToEvaluation = (hexCode) =>
    navigate(
      path({
        name: 'user_module_totalEnergiesRisk_risk_list',
        params: {
          moduleBridgeHex,
          page: 1,
          filtersHex: hexCode,
        },
      })
    );

  // > GENERATE FILTER HEX CODE
  const generateFilterHexCode = async (values, setSubmitting) =>
    axios
      .post(
        axiosPath(
          'api_r_user_module_totalEnergiesRisk_risk_generateFilterHexCode'
        ),
        { moduleBridgeHex, filters: values }
      )
      .then(({ data }) => {
        setSubmitting(false);
        goToEvaluation(data.hexCode);
        dispatch(currentPageAction.change('auxSidebar', { opened: null }));
      });

  const handleOnSubmit = (values, { setSubmitting }) => {
    generateFilterHexCode(values, setSubmitting);
  };
  // < GENERATE FILTER HEX CODE

  // > FILTERS
  const getFilters = useCallback(
    async () =>
      axios
        .post(
          axiosPath('api_r_user_module_totalEnergiesRisk_risk_getFilters'),
          {
            moduleBridgeHex,
            filtersHex,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(currentPageAction.change('riskFilters', data));
          }
        }),
    [moduleBridgeHex, filtersHex]
  );

  useEffect(() => {
    if (filtersHex !== '0') {
      getFilters();
    } else {
      dispatch(
        currentPageAction.change(
          'riskFilters',
          getTotalEnergiesRiskRiskFilterInitialValues(),
          true
        )
      );
    }
  }, [filtersHex]);
  // < FILTERS

  return (
    <>
      <li
        className={auxSidebar && auxSidebar === 'filters' ? 'active' : ''}
        onClick={() => handleAuxSidebar()}
      >
        <span className="sidebar-element">
          <AppRemixIcon icon="equalizer" className="sidebar-icon" />{' '}
          <span className="sb-title">
            <Translator file="module-legalCompliance" name="filtering" />
          </span>
        </span>
      </li>
      <AuxSidebar
        title={<Translator file="module-legalCompliance" name="filtering" />}
        isOpen={auxSidebar && auxSidebar === 'filters'}
        width="350px"
        position="right"
        onClose={handleAuxSidebar}
      >
        {openOnce && (
          <>
            {filters && (
              <Formik
                validateOnChange={false}
                validateOnBlur={false}
                enableReinitialize
                initialValues={filters}
                onSubmit={handleOnSubmit}
              >
                {({
                  setFieldValue,
                  handleSubmit,
                  values,
                  errors,
                  isSubmitting,
                }) => (
                  <>
                    <div className="d-flex mt-3 mb-3">
                      <span
                        className="btn btn-info btn-block"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        <Translator
                          file="module-legalCompliance"
                          name="filtering"
                        />
                      </span>
                      {filtersHex !== '0' && (
                        <span
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="delete.filters"
                            />
                          )}
                          className="btn btn-outline-danger ml-1"
                          onClick={() =>
                            navigate(
                              path({
                                name: 'user_module_totalEnergiesRisk_risk_list',
                                params: {
                                  moduleBridgeHex,
                                  page: 1,
                                  filtersHex: 0,
                                },
                              })
                            )
                          }
                        >
                          <AppRemixIcon icon="delete-bin-6" />
                        </span>
                      )}
                    </div>
                    <AppInput
                      title="Código"
                      icon={<AppRemixIcon icon="fingerprint" />}
                      value={values.code}
                      onChange={(opt) => setFieldValue('code', opt)}
                      isClearable
                    />
                    <AppInput
                      title="Descripción"
                      value={values.description}
                      error={errors.description}
                      onChange={(opt) => setFieldValue('description', opt)}
                      isClearable
                    />
                    <AppInput
                      title="Causas"
                      value={values.causes}
                      error={errors.causes}
                      onChange={(opt) => setFieldValue('causes', opt)}
                      isClearable
                    />
                    <AppInput
                      title="Consecuencias"
                      value={values.consequences}
                      error={errors.consequences}
                      onChange={(opt) => setFieldValue('consequences', opt)}
                      isClearable
                    />
                    <TotalEnergiesRiskRiskSelect
                      title="Riesgos"
                      value={values?.riskIDs}
                      error={errors?.riskIDs}
                      onHandleChange={(opt) => setFieldValue('riskIDs', opt)}
                      hasSearchBox
                      isClearable
                      isMulti
                    />
                    {permissionLevel === 0 && (
                      <>
                        <TotalEnergiesRiskProcessSelect
                          title="Procesos"
                          value={values?.process}
                          error={errors?.process}
                          onHandleChange={(opt) => {
                            setFieldValue('process', opt);
                          }}
                          hasSearchBox
                          hasSelectOptions
                          isMulti
                          onlyParents
                          isDisabled={values?.subProcess?.length > 0}
                        />
                        <TotalEnergiesRiskProcessSelect
                          title="Subprocesos"
                          value={values?.subProcess}
                          error={errors?.subProcess}
                          onHandleChange={(opt) => {
                            setFieldValue('subProcess', opt);
                          }}
                          hasSearchBox
                          hasSelectOptions
                          isMulti
                          isDisabled={values?.process?.length > 0}
                        />
                      </>
                    )}
                    <AppMultiSelect
                      options={riskTypeOptions}
                      title="Tipo de riesgo"
                      value={values?.riskType}
                      onChange={(opt) => {
                        setFieldValue('riskType', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={stateOptions}
                      title="Estado de riesgo"
                      value={values?.state}
                      onChange={(opt) => {
                        setFieldValue('state', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={probabilityOptions}
                      title="Probabilidad"
                      value={values?.probability}
                      onChange={(opt) => {
                        setFieldValue('probability', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={inherentImpactOptions}
                      title="Impacto inherente"
                      value={values?.inherentImpact}
                      onChange={(opt) => {
                        setFieldValue('inherentImpact', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={severityOptions}
                      title="Severidad"
                      value={values?.severity}
                      onChange={(opt) => {
                        setFieldValue('severity', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={priorityOptions}
                      title="Prioridad"
                      value={values?.priority}
                      onChange={(opt) => {
                        setFieldValue('priority', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppSingleSelect
                      options={noYesOptions}
                      title="¿Existen controles?"
                      value={values?.hasControls}
                      error={errors?.hasControls}
                      onChange={(opt) => {
                        setFieldValue('hasControls', opt);
                      }}
                      isClearable
                    />
                    {permissionLevel === 0 && (
                      <AppSingleSelect
                        options={noYesOptions}
                        title="Mostrar desactivados"
                        value={values?.active}
                        error={errors?.active}
                        onChange={(opt) => {
                          setFieldValue('active', opt);
                        }}
                      />
                    )}
                  </>
                )}
              </Formik>
            )}
          </>
        )}
      </AuxSidebar>
    </>
  );
}
